import { Drawer, notification } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMap } from "react-map-gl/mapbox";
import GlobalStoreContext from "../../../context/GlobalStoreContext";
import { Area } from "../../../models/Area";
import { Dropoff } from "../../../models/Dropoff";
import { GateToDropoffRoute } from "../../../models/GateToDropoffRoute";
import { HaulRoute } from "../../../models/HaulRoute";
import { WaypointStep } from "../../../models/WaypointStep";
import {
    checkApproachingWaypoint,
    checkDepartingWaypoint,
    remainingRouteDistanceMeters,
} from "../../Helper/WaypointGuidance";
import DamnMap from "../damnMap";
import EnableLocationServices from "../enableLocationServices";
import WaypointGuidanceBanner from "./WaypointGuidanceBanner";
import WaypointGuidanceBottomBar from "./WaypointGuidanceBottomBar";

interface WaypointGuidanceProps {
    active: boolean;
    cancel: () => void;
    route: Area | HaulRoute | GateToDropoffRoute;
    dropoff?: Dropoff;
    continuationRoutes?: GateToDropoffRoute[];
}

const visibleStepsFor = (route: Area | HaulRoute | GateToDropoffRoute) =>
    (route.waypointDirection?.waypointSteps ?? [])
        .filter((step) => !step.hidden)
        .sort((a, b) => a.stepNumber - b.stepNumber);

const WaypointGuidance: React.FC<WaypointGuidanceProps> = (props) => {
    const context = React.useContext(GlobalStoreContext);
    // Set once the driver carries on from props.route onto one of its continuation routes.
    const [continuedRoute, setContinuedRoute] = useState<GateToDropoffRoute | null>(null);
    const currentRoute = continuedRoute ?? props.route;
    const waypointMapObject = props.route.getMapObject(context);
    const dropOffMapObject = props.dropoff ? props.dropoff.getMapObject(context) : null;
    const [activeStep, setActiveStep] = useState<number>(1);
    const [userTrackingModalOpen, setUserTrackingModalOpen] = React.useState<boolean>(false);
    const [arrived, setArrived] = React.useState<boolean>(false);
    const [currentStage, setCurrentStage] = useState<"approaching" | "departing">("approaching");
    const [distanceToNextWaypoint, setDistanceToNextWaypoint] = useState<number>(0);

    const visibleSteps = useMemo(() => visibleStepsFor(currentRoute), [currentRoute]);

    const availableContinuations = useMemo(
        () =>
            continuedRoute ? [] : (props.continuationRoutes ?? []).filter((route) => visibleStepsFor(route).length > 0),
        [continuedRoute, props.continuationRoutes]
    );

    const continuationMapObjects = useMemo(
        () =>
            (props.continuationRoutes ?? [])
                .map((route) => route.getMapObject(context))
                .filter((mapObject): mapObject is NonNullable<typeof mapObject> => !!mapObject),
        [props.continuationRoutes, context]
    );

    const { waypointMap } = useMap();

    // Wakelock prevents phone from going to sleep during waypoint guidance
    const wakeLockRef = useRef<WakeLockSentinel | null>(null);

    const hasWakeLock = (
        nav: Navigator
    ): nav is Navigator & { wakeLock: { request(type: "screen"): Promise<WakeLockSentinel> } } => {
        return "wakeLock" in nav;
    };

    const requestWakeLock = async () => {
        if (!hasWakeLock(navigator)) {
            // Wake Lock API not supported; avoid calling into unsupported API
            return;
        }

        try {
            const wakeLock = await navigator.wakeLock.request("screen");
            // Store the wake lock in a ref so it persists across renders
            wakeLockRef.current = wakeLock;
        } catch (err) {
            // wakeLock failed
        }
    };
    const releaseWakeLock = async () => {
        if (wakeLockRef.current) {
            wakeLockRef.current
                .release()
                .catch(() => {
                    // Ignoring release errors since wake lock is a best-effort feature
                })
                .finally(() => {
                    wakeLockRef.current = null;
                });
        }
    };

    useEffect(() => {
        context.setCurrentWaypointStep(visibleSteps[activeStep - 1] || null);
    }, [visibleSteps, activeStep, props.active]);

    useEffect(() => {
        if (props.active) {
            requestWakeLock();
            // Timeout ensures that the transition has completed and the div is rendered
            // This prevents map resizing issues

            setTimeout(() => {
                resizeMap(0);
            }, 700);
        }
        return () => {
            setActiveStep(1);
            setArrived(false);
            setContinuedRoute(null);
            context.setUserLocation(null);
            context.setCurrentWaypointStep(null);
            releaseWakeLock();
        };
    }, [props.active]);

    const resizeMap = (retryCount = 0) => {
        if (retryCount > 10) {
            return;
        }
        if (waypointMap?.getMap()) {
            waypointMap.resize();
        } else {
            setTimeout(() => resizeMap(retryCount + 1), 500);
        }
    };

    useEffect(() => {
        if (props.active) {
            // Load modal to prompt user for location after the screen has been active for 1 second
            setTimeout(() => {
                setUserTrackingModalOpen(true);
            }, 1500);
        }
    }, [props.active]);

    useEffect(() => {
        // Ensure that when the active step changes (e.g. via manual navigation),
        // we always start that step in the "approaching" stage.
        setCurrentStage("approaching");
        setArrived(false);
    }, [activeStep]);

    const geolocateUser = () => {
        try {
            context.geoControlRef.current?.trigger();
        } catch (e) {
            notification.error({
                message: "Error accessing location",
                description:
                    "There was an error accessing your location. Please ensure that location services are enabled for this site in your browser settings.",
            });
            return;
        }
        setUserTrackingModalOpen(false);
    };

    const toggleLocation = () => {
        if (!context.userLocation) {
            setUserTrackingModalOpen(true);
            return;
        }
        context.geoControlRef.current?.trigger();
        // The control doesn't report being switched off, so clear the stale fix ourselves.
        context.setUserLocation(null);
    };

    const changeStep = (step: number) => {
        // Stepping past the end without location still needs a way onto the next leg.
        if (step === visibleSteps.length + 1 && availableContinuations.length > 0) {
            setArrived(true);
            return;
        }
        if (step < 1 || step > visibleSteps.length) {
            return;
        }
        setActiveStep(step);
    };

    const continueOnto = (route: GateToDropoffRoute) => {
        setContinuedRoute(route);
        // Reset explicitly, since the step-change effect won't fire if we were already on step 1.
        setActiveStep(1);
        setCurrentStage("approaching");
        setDistanceToNextWaypoint(0);
        setArrived(false);
    };

    const approachingLogic = (waypointStep: WaypointStep, userLocation: GeolocationCoordinates) => {
        if (!userLocation || !waypointStep.coordinates) {
            return;
        }

        const waypointResult = checkApproachingWaypoint(
            [userLocation.longitude, userLocation.latitude],
            waypointStep.coordinates!
        );
        if (waypointResult.isWithinProximity && waypointStep === visibleSteps.at(-1)) {
            setArrived(true);
            return;
        }

        if (waypointResult.isWithinProximity) {
            setCurrentStage("departing");
            setDistanceToNextWaypoint(0);
        } else {
            setDistanceToNextWaypoint(waypointResult.distanceMeters);
        }
    };

    const departingLogic = (waypointStep: WaypointStep, userLocation: GeolocationCoordinates) => {
        if (!userLocation || !waypointStep.coordinates) {
            return;
        }
        const hasDeparted = checkDepartingWaypoint(
            [userLocation.longitude, userLocation.latitude],
            waypointStep.coordinates!
        );
        if (hasDeparted) {
            setCurrentStage("approaching");
            setActiveStep(activeStep + 1);
        }
    };

    const checkGeolocateNextStep = (userLocation: GeolocationCoordinates | null) => {
        if (!userLocation) {
            return;
        }
        const currentWaypointStep = visibleSteps[activeStep - 1];
        if (!currentWaypointStep?.coordinates) {
            return;
        }

        if (currentStage === "approaching") {
            approachingLogic(currentWaypointStep, userLocation);
            return;
        } else if (currentStage === "departing") {
            departingLogic(currentWaypointStep, userLocation);
            return;
        }
    };

    useEffect(() => {
        checkGeolocateNextStep(context.userLocation);
    }, [context.userLocation, activeStep, currentStage]);

    const remainingMeters = useMemo(() => {
        const upcomingCoordinates = visibleSteps
            .slice(activeStep - 1)
            .map((step) => step.coordinates)
            .filter((coordinates): coordinates is number[] => !!coordinates);
        const startCoordinates = context.userLocation
            ? [context.userLocation.longitude, context.userLocation.latitude]
            : (visibleSteps[activeStep - 2]?.coordinates ?? null);
        return remainingRouteDistanceMeters(upcomingCoordinates, startCoordinates);
    }, [visibleSteps, activeStep, context.userLocation]);

    const currentWaypointStep = visibleSteps[activeStep - 1];

    const mapComponent = useMemo(() => {
        return (
            <DamnMap
                mapId="waypointMap"
                mapObjects={[
                    waypointMapObject,
                    ...continuationMapObjects,
                    ...(dropOffMapObject ? [dropOffMapObject] : []),
                ]}
                route={currentRoute}
                stepRoutes={(props.continuationRoutes ?? []).filter((route) => route !== currentRoute)}
                currentStep={activeStep}
                geoControlRef={context.geoControlRef}
                setUserLocation={context.setUserLocation}
                navigationMode
                navigationTarget={currentWaypointStep?.coordinates}
                mapStyleOverride="mapbox://styles/mapbox/navigation-day-v1"
            />
        );
    }, [
        waypointMapObject,
        continuationMapObjects,
        dropOffMapObject,
        currentRoute,
        props.continuationRoutes,
        activeStep,
        currentWaypointStep,
        props.active,
        context.geoControlRef,
        context.setUserLocation,
    ]);

    return (
        <Drawer
            placement="top"
            open={props.active}
            onClose={props.cancel}
            zIndex={999}
            styles={{ body: { margin: 0, padding: 0 } }}
            closable={false}
            height="100%"
        >
            <div className="h-full flex flex-col overflow-hidden bg-plot-white">
                <div className="flex-1 relative">
                    {waypointMapObject ? (
                        mapComponent
                    ) : (
                        <div className="bg-linear-to-r from-blue-400 to-green-600 w-full h-full" />
                    )}
                    {currentWaypointStep && (
                        <WaypointGuidanceBanner
                            waypointStep={currentWaypointStep}
                            nextWaypointStep={visibleSteps[activeStep] ?? null}
                            stepIndex={activeStep}
                            totalSteps={visibleSteps.length}
                            distanceMeters={context.userLocation ? distanceToNextWaypoint : null}
                            changeStep={changeStep}
                            hasContinuation={availableContinuations.length > 0}
                        />
                    )}
                </div>

                <WaypointGuidanceBottomBar
                    steps={visibleSteps}
                    activeStep={activeStep}
                    setActiveStep={changeStep}
                    remainingMeters={remainingMeters}
                    hasUserLocation={!!context.userLocation}
                    toggleLocation={toggleLocation}
                    arrived={arrived}
                    // A haul route ends at the jobsite, not the dropoff, so only name the dropoff on the last leg.
                    destinationName={currentRoute instanceof HaulRoute ? null : props.dropoff?.name}
                    continuationRoutes={availableContinuations}
                    continueOnto={continueOnto}
                    exit={props.cancel}
                />

                <EnableLocationServices
                    open={userTrackingModalOpen}
                    toggleOpen={() => setUserTrackingModalOpen(!userTrackingModalOpen)}
                    handleGeolocateAllow={geolocateUser}
                />
            </div>
        </Drawer>
    );
};
export default WaypointGuidance;
